import { useQuery } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { authApi } from '@/features/auth/api/auth.api';
import { useAuthStore } from '@/store/auth';
import {
  guideProfileApi,
  guideProfileKeys,
} from '@/features/guide-profile/api/guide-profile.api';
import {
  MarketplaceStatus,
  ProfileStatus,
  VerificationStatus,
} from '@/types';
import type { GuideJourneyStage } from '../components/GuideJourneySteps';
import {
  GuideJourneyScreen,
  type GuideJourneyTone,
} from '../components/GuideJourneyScreen';
import { GUIDE_PROFILE_SETUP_ROUTE } from '@/features/auth/utils/resolvePostAuthRoute';

interface StatusView {
  tone: GuideJourneyTone;
  stage: GuideJourneyStage;
  title: string;
  body: string;
  /** Shown verbatim to the guide — must never contain internal detail. */
  note?: string;
  action?: { label: string; to: string };
}

/**
 * Translates lifecycle enums into plain language. A raw status string such as
 * `PROFILE_INCOMPLETE` or `UNDER_REVIEW` must never reach the guide.
 */
function resolveStatusView(input: {
  verificationStatus: VerificationStatus;
  profileStatus: ProfileStatus;
  marketplaceStatus: MarketplaceStatus;
  adminNotes?: string;
}): StatusView {
  const { verificationStatus, profileStatus, marketplaceStatus, adminNotes } = input;

  if (marketplaceStatus === MarketplaceStatus.SUSPENDED) {
    return {
      tone: 'error',
      stage: 'done',
      title: 'Your Guide profile is suspended',
      body: 'Your profile is not visible to travellers at the moment. Please contact Trplin support if you believe this is incorrect.',
    };
  }

  if (verificationStatus === VerificationStatus.FAILED) {
    return {
      tone: 'error',
      stage: 'verification',
      title: "We're unable to complete your verification",
      body: "We're unable to complete your verification at this time. Please contact Trplin support if you believe this is incorrect.",
    };
  }

  if (
    verificationStatus === VerificationStatus.NOT_STARTED ||
    verificationStatus === VerificationStatus.PENDING
  ) {
    return {
      tone: 'waiting',
      stage: 'verification',
      title: 'Your application is under review',
      body: "We've received your information and your background verification is being arranged. Our verification partner will reach out to you directly — there's nothing you need to send us, and we'll notify you when you can continue.",
    };
  }

  if (verificationStatus === VerificationStatus.IN_PROGRESS) {
    return {
      tone: 'waiting',
      stage: 'verification',
      title: 'Your background verification is in progress',
      body: "Our verification partner is completing your checks and may contact you directly if they need anything. We'll notify you as soon as it is complete.",
    };
  }

  // Verification passed from here on.
  switch (profileStatus) {
    case ProfileStatus.NOT_STARTED:
      return {
        tone: 'success',
        stage: 'profile',
        title: 'Your background verification is complete',
        body: 'You can now build your Guide profile and tell travellers what makes your tours special.',
        action: { label: 'Complete your profile', to: GUIDE_PROFILE_SETUP_ROUTE },
      };

    case ProfileStatus.IN_PROGRESS:
      return {
        tone: 'action',
        stage: 'profile',
        title: 'Your Guide profile is half finished',
        body: 'Pick up where you left off — everything you entered has been saved.',
        action: { label: 'Continue your profile', to: GUIDE_PROFILE_SETUP_ROUTE },
      };

    case ProfileStatus.CHANGES_REQUIRED:
      return {
        tone: 'action',
        stage: 'review',
        title: 'We need a few updates before your profile can go live',
        body: 'Our team reviewed your profile and asked for some changes. Once you update and resubmit, we will take another look.',
        note: adminNotes,
        action: { label: 'Update profile', to: GUIDE_PROFILE_SETUP_ROUTE },
      };

    case ProfileStatus.SUBMITTED:
    case ProfileStatus.UNDER_REVIEW:
      return {
        tone: 'waiting',
        stage: 'review',
        title: 'Your Guide profile is being reviewed',
        body: 'Your profile has been submitted successfully. Our team expects to complete the review within 24 hours, and we will notify you as soon as it is approved.',
      };

    case ProfileStatus.APPROVED:
      return {
        tone: 'success',
        stage: 'done',
        title: "You're a Trplin Guide 🎉",
        body: 'Your profile is approved. Head to your dashboard to set your pricing and go live.',
        action: { label: 'Go to Guide Dashboard', to: '/dashboard' },
      };

    default:
      return {
        tone: 'waiting',
        stage: 'verification',
        title: 'Your application is under review',
        body: "We'll notify you when there is an update.",
      };
  }
}

export function GuideApplicationStatusPage() {
  const navigate = useNavigate();
  const { user, isApprovedGuide } = useAuth();
  const setMe = useAuthStore((state) => state.setMe);

  const { data, isLoading, isError } = useQuery({
    queryKey: guideProfileKeys.status(user?.id),
    queryFn: guideProfileApi.getStatus,
    enabled: Boolean(user?.id),
    // The guide is waiting on an admin decision made elsewhere, so re-check
    // whenever they return to the tab rather than making them reload.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // An approved guide belongs on the dashboard, not on a status screen.
  if (isApprovedGuide) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 3 }} />
      </Container>
    );
  }

  if (isError || !data) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">
          We couldn&apos;t load your application status. Please refresh and try again.
        </Alert>
      </Container>
    );
  }

  if (!data.hasApplication) {
    return <Navigate to="/become-guide" replace />;
  }

  const view = resolveStatusView(data);

  const handleAction = async (to: string) => {
    // Refresh the session first so the destination's guard sees current state.
    try {
      const me = await authApi.getMe();
      setMe(me);
    } catch {
      // Navigate anyway; the guard will correct us if we are out of date.
    }
    navigate(to);
  };

  return (
    <GuideJourneyScreen
      stage={view.stage}
      tone={view.tone}
      title={view.title}
      body={view.body}
      note={view.note}
      primaryAction={
        view.action
          ? {
              label: view.action.label,
              intent: 'primary-gold',
              onClick: () => void handleAction(view.action!.to),
            }
          : undefined
      }
    />
  );
}
